import React, { useState, useEffect } from 'react';
import { Outlet, Navigate, useLocation, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useAuth } from '@context/AuthContext';
import { StudentThemeProvider, useStudentTheme } from '@context/StudentThemeContext';
import { LogOut, Menu, X, ChevronDown, Sun, Moon, Lock, Unlock } from 'lucide-react';
import { Images } from '@assets';
import { studentService } from '@services/student/studentService';
import { useStudentDashboard } from '@services/student/studentQueries';
import StudentAnimatedBackground from '@components/StudentPortal/StudentAnimatedBackground';
import { useRipple } from '@/hooks/useRipple';
import { getModuleIcon } from '@/pages/ModuleTemplatesPage/iconHelper';
import { JourneyShell, JourneyBoot } from '@components/StudentJourney';
import { resolvePortalVersion } from './resolvePortalVersion';

const StudentLayout: React.FC = () => (
    <StudentThemeProvider>
        <StudentLayoutGate />
    </StudentThemeProvider>
);

/**
 * Picks the Student Portal experience per cohort (AIP-18 / Cohort 2 rollout).
 *
 * CLASSIC (Cohort 1, the default) renders `StudentLayoutInner` below — completely
 * unchanged, and without waiting on this gate's query (it does its own fetch),
 * so existing students are byte-for-byte unaffected. Only a cohort explicitly
 * flagged `student_portal_version: "JOURNEY"` (or `?portal=journey` in a DEV
 * build) reaches the new `JourneyShell`.
 */
const StudentLayoutGate: React.FC = () => {
    const { user } = useAuth();
    const location = useLocation();
    const isStudent = !!user && user.role_name?.toUpperCase() === 'STUDENT';

    const { data, isLoading } = useStudentDashboard(isStudent && !user?.force_password_change);

    if (!isStudent) return <Navigate to="/login" replace />;
    if (user!.force_password_change && location.pathname !== '/student/change-password') {
        return <Navigate to="/student/change-password" replace />;
    }

    const version = resolvePortalVersion(data, location.search);

    if (version === 'CLASSIC') return <StudentLayoutInner dashboard={data} />;
    if (!data && isLoading) return <JourneyBoot />;
    return <JourneyShell dashboard={data} />;
};

const StudentLayoutInner: React.FC<{ dashboard?: any }> = ({ dashboard }) => {
    const { t } = useTranslation('students');
    const { user, logout } = useAuth();
    const location = useLocation();
    const { theme, toggleTheme } = useStudentTheme();
    const ripple = useRipple();
    const navigate = useNavigate();

    const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
    const [showProfile, setShowProfile] = useState(false);
    const [studentData, setStudentData] = useState<any>(dashboard || null);

    useEffect(() => {
        if (dashboard) {
            setStudentData(dashboard);
        }
    }, [dashboard]);

    const isSkillProfileLocked = studentData?.profile_completion ? !studentData.profile_completion.is_complete : false;

    const isOnTasksRoute = location.pathname.startsWith('/student/tasks') || location.pathname.startsWith('/student/weeks');
    const isOnNotebookRoute = location.pathname.startsWith('/student/notebook');

    const getProfilePictureUrl = (path: string | null | undefined) => {
        if (!path) return '';
        if (path.startsWith('http')) return path;
        const backendBase = (import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:5050/api/v1').replace('/api/v1', '');
        return `${backendBase}/${path}`;
    };
    const avatarUrl = user?.profile_picture ? getProfilePictureUrl(user.profile_picture) : '';

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 768) {
                setIsSidebarOpen(true);
            } else {
                setIsSidebarOpen(false);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const fetchStudentData = () => {
            if (user && user.role_name?.toUpperCase() === 'STUDENT') {
                studentService.getDashboard()
                    .then(res => setStudentData(res.data.data))
                    .catch(err => console.error("Error fetching student layout data", err));
            }
        };

        const handleRealTimeUpdate = (event: any) => {
            if (event?.detail?.profile_completion) {
                setStudentData((prev: any) => ({
                    ...(prev || {}),
                    profile_completion: event.detail.profile_completion,
                }));
            }
            fetchStudentData();
        };

        fetchStudentData();

        window.addEventListener('student-profile-completion-updated', handleRealTimeUpdate);
        return () => window.removeEventListener('student-profile-completion-updated', handleRealTimeUpdate);
    }, [user, location.pathname]);

    if (!user || user.role_name?.toUpperCase() !== 'STUDENT') {
        return <Navigate to="/login" replace />;
    }

    // Force password change if required
    if (user.force_password_change && location.pathname !== '/student/change-password') {
        return <Navigate to="/student/change-password" replace />;
    }

    // A CLASSIC (Cohort 1) student who hand-types a JOURNEY-only URL gets bounced
    // to their dashboard rather than seeing a Journey page in the CLASSIC shell.
    if (/^\/student\/(journey|learn|code|calendar|achievements)(\/|$)/.test(location.pathname)) {
        return <Navigate to="/student/dashboard" replace />;
    }

    const handleLogout = async () => {
        await logout();
        window.location.href = '/login';
    };

    return (
        <div className={`student-portal-shell relative flex h-screen bg-[var(--sp-canvas)] overflow-hidden ${theme === 'dark' ? 'student-dark' : ''}`}>
            <StudentAnimatedBackground />

            {/* Sidebar Backdrop for Mobile */}
            {isSidebarOpen && (
                <div 
                    className="fixed inset-0 z-40 bg-black/40 backdrop-blur-xs md:hidden"
                    onClick={() => setIsSidebarOpen(false)}
                />
            )}

            {/* Student Sidebar */}
            <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-border shadow-soft flex flex-col transition-transform duration-300 ${
                isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
            }`}>
                {/* Logo Section */}
                <div className="p-5 border-b border-border flex items-center justify-center relative">
                    <img src={Images.tpaLogo} alt="TPA Logo" className={`h-9 w-auto object-contain ${theme === 'dark' ? '' : 'invert brightness-90 hue-rotate-[220deg]'}`} />
                    <button
                        className="absolute right-5 md:hidden text-slate-body hover:text-navy hover:bg-blue-bg p-1 rounded-lg transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
                        onClick={() => setIsSidebarOpen(false)}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Navigation Links */}
                <nav className="flex-1 py-6 px-4 space-y-1.5">
                    <div className="text-xs font-semibold text-slate-body uppercase tracking-wider mb-4 px-2">
                        {t('portal.layout.myLearning')}
                    </div>

                    {/* Dynamically render modules from student dashboard or fallback */}
                    {(studentData?.sidebar_modules && studentData.sidebar_modules.length > 0
                        ? studentData.sidebar_modules
                        : [
                              { title: t('portal.layout.dashboard') || 'Dashboard', route: '/student/dashboard', icon: 'BookOpen', slug: 'dashboard' },
                              { title: 'Calender & Recordings', route: '/student/recordings', icon: 'Video', slug: 'recordings' },
                              { title: 'Coding Tasks', route: '/student/weeks/1', icon: 'Code2', slug: 'coding-tasks' },
                              { title: 'Python Lab', route: '/student/notebook', icon: 'Terminal', slug: 'python-lab' },
                              { title: 'Skill Profile', route: '/student/skill-profile', icon: 'Award', slug: 'skill-profile' },
                              { title: 'Edit Profile', route: '/student/profile', icon: 'User', slug: 'profile' },
                          ]
                    ).map((mod: any) => {
                        const IconComponent = getModuleIcon(mod.icon);
                        const isSkillProfile = mod.slug === 'skill-profile' || mod.route === '/student/skill-profile';
                        const isLocked = isSkillProfile && isSkillProfileLocked;
                        const isRouteActive =
                            location.pathname === mod.route ||
                            (mod.slug === 'coding-tasks' && isOnTasksRoute) ||
                            (mod.route.startsWith('/student/weeks') && isOnTasksRoute) ||
                            (mod.route.startsWith('/student/notebook') && isOnNotebookRoute);

                        const handleClick = (e: React.MouseEvent) => {
                            if (isLocked) {
                                e.preventDefault();
                                toast.error('Please complete your profile section to 100% after that unlock', {
                                    id: 'skill-profile-locked',
                                    duration: 4500,
                                    icon: '🔒',
                                });
                                navigate('/student/profile');
                                if (window.innerWidth < 768) setIsSidebarOpen(false);
                                return;
                            }
                            if (window.innerWidth < 768) setIsSidebarOpen(false);
                        };

                        return (
                            <Link
                                key={mod.slug || mod.route}
                                to={isLocked ? '/student/profile' : mod.route}
                                onClick={handleClick}
                                onPointerDown={ripple.onPointerDown}
                                title={isLocked ? 'Please complete your profile section to 100% after that unlock' : undefined}
                                className={`ripple-trigger flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl border-l-2 font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 ${
                                    isRouteActive
                                        ? 'bg-blue-bg text-primary border-primary'
                                        : 'border-transparent text-slate-body hover:bg-blue-bg/60 hover:text-navy'
                                }`}
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    <IconComponent size={18} className="shrink-0" />
                                    <span className="truncate">{mod.title}</span>
                                </div>
                                {isSkillProfile && (
                                    isLocked ? (
                                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 shrink-0">
                                            <Lock size={10} />
                                            <span>Locked</span>
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 shrink-0">
                                            <Unlock size={10} />
                                            <span>Unlocked</span>
                                        </span>
                                    )
                                )}
                            </Link>
                        );
                    })}
                </nav>

                {/* Collapsible Profile Details Card */}
                <div className="px-4 py-3 border-t border-border bg-blue-bg/40">
                    <button
                        onClick={() => setShowProfile(prev => !prev)}
                        className="flex items-center justify-between w-full p-2 rounded-xl hover:bg-blue-bg transition-colors text-left cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
                    >
                        <div className="flex items-center gap-2 overflow-hidden">
                            {avatarUrl ? (
                                <img src={avatarUrl} alt="Avatar" className="w-8 h-8 rounded-full object-cover shrink-0" />
                            ) : (
                                <div className="w-8 h-8 rounded-full bg-blue-bg text-primary flex items-center justify-center font-bold text-sm shrink-0">
                                    {user?.name?.[0]?.toUpperCase() || 'S'}
                                </div>
                            )}
                            <div className="overflow-hidden">
                                <span className="block font-semibold text-xs text-navy truncate">{user?.name}</span>
                                <span className="block text-[10px] text-slate-body truncate">Student Profile</span>
                            </div>
                        </div>
                        <ChevronDown size={16} className={`text-slate-body shrink-0 transition-transform ${showProfile ? 'rotate-180' : ''}`} />
                    </button>

                    {showProfile && (
                        <div className="mt-3 p-3 bg-white border border-border rounded-xl text-[11px] space-y-2.5 text-slate-body shadow-soft max-h-[220px] overflow-y-auto">
                            <div>
                                <span className="block font-medium text-[9px] text-slate-body/80 uppercase tracking-wider">Email</span>
                                <span className="font-semibold text-navy break-all">{user?.email}</span>
                            </div>
                            {studentData?.enrollment && (
                                <div>
                                    <span className="block font-medium text-[9px] text-slate-body/80 uppercase tracking-wider">Enrollment No.</span>
                                    <span className="font-mono font-semibold text-navy">{studentData.enrollment.number}</span>
                                </div>
                            )}
                            {studentData?.cohort && (
                                <div>
                                    <span className="block font-medium text-[9px] text-slate-body/80 uppercase tracking-wider">Cohort</span>
                                    <span className="font-semibold text-navy">{studentData.cohort.title}</span>
                                </div>
                            )}
                            {studentData?.enrollment?.mentor && (
                                <div>
                                    <span className="block font-medium text-[9px] text-slate-body/80 uppercase tracking-wider">Assigned Mentor</span>
                                    <span className="font-semibold text-emerald-700">{studentData.enrollment.mentor.name}</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Logout Button */}
                <div className="p-4 border-t border-border">
                    <button
                        onClick={handleLogout}
                        onPointerDown={ripple.onPointerDown}
                        className="ripple-trigger flex items-center gap-3 px-3 py-2.5 w-full rounded-xl text-slate-body hover:bg-red-50 hover:text-red-500 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
                    >
                        <LogOut size={18} />
                        <span className="font-medium">{t('portal.layout.logout')}</span>
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <main className={`flex-1 overflow-y-auto flex flex-col transition-all duration-300 ${isSidebarOpen ? 'md:pl-64' : 'md:pl-0'}`}>
                {/* Top Header */}
                <header className="sticky top-0 z-30 bg-white border-b border-border p-4 flex justify-between items-center shrink-0">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => setIsSidebarOpen(prev => !prev)}
                            onPointerDown={ripple.onPointerDown}
                            className="ripple-trigger text-slate-body cursor-pointer hover:bg-blue-bg hover:text-navy p-1.5 rounded-xl transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
                        >
                            <Menu size={24} />
                        </button>
                        <img
                            src={Images.tpaLogo}
                            alt="TPA Logo"
                            className={`h-7 w-auto object-contain transition-opacity duration-200 ${theme === 'dark' ? '' : 'invert brightness-90 hue-rotate-[220deg]'} ${isSidebarOpen ? 'md:opacity-0 md:pointer-events-none' : 'opacity-100'}`}
                        />
                    </div>
                    <div className="flex items-center gap-1.5">
                        <button
                            onClick={toggleTheme}
                            onPointerDown={ripple.onPointerDown}
                            aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
                            title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
                            className="ripple-trigger text-slate-body hover:text-primary cursor-pointer hover:bg-blue-bg p-1.5 rounded-xl transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
                        >
                            {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
                        </button>
                        <button
                            onClick={handleLogout}
                            onPointerDown={ripple.onPointerDown}
                            className="ripple-trigger text-slate-body hover:text-red-500 cursor-pointer hover:bg-red-50 p-1.5 rounded-xl transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
                        >
                            <LogOut size={20} />
                        </button>
                    </div>
                </header>

                <div className="p-6 max-w-6xl mx-auto w-full flex-1">
                    <Outlet />
                </div>
            </main>
        </div>
    );
};

export default StudentLayout;
